import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MDEditor from "@uiw/react-md-editor";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import "./../../styles/common/markdownEditorPopup.scss";
import AddImagePopup from "./AddImagePopup";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";

const MarkdownEditorPopup = ({
  open,
  handleClose,
  title,
  initialValue = "",
  onSave,
  context,
  pageId,
  pageData,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
}) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectionMap, setSelectionMap] = useState({});
  const [imagePopupState, setImagePopupState] = useState({
    open: false,
    sectionIndex: null,
  });

  const isSaveDisabled =
    saving ||
    items.length === 0 ||
    items.every((it) => !String(it.content || "").trim());

  const mdPreviewOptions = {
    remarkPlugins: [remarkMath],
    rehypePlugins: [rehypeKatex],
  };

  const doUpdate = async (contentArray) => {
    const payload = { id: pageId, content: contentArray };
    let updatedPageData;
    switch (context) {
      case "course":
        await updatePageForCourse(payload);
        updatedPageData = await getPageForCourse(courseKey, pageId);
        break;
      case "subject":
        await updatePageForSubject(payload);
        updatedPageData = await getPageForSubject(subjectKey, pageId);
        break;
      case "topic":
        await updatePageForTopic(payload);
        updatedPageData = await getPageForTopic(topicKey, pageId);
        break;
      case "subtopic":
        await updatePageForSubtopic(payload);
        updatedPageData = await getPageForSubtopic(subTopicKey, pageId);
        break;
      default:
        return onSave ? onSave(contentArray) : null;
    }

    if (onSave) onSave(updatedPageData);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const contentArray = items.map((it) => ({
        type: it.type || (pageData && pageData.page_type) || "content",
        content: it.content || "",
      }));
      await doUpdate(contentArray);
    } catch (e) {
      console.error("Failed to save content:", e);
    } finally {
      setSaving(false);
      handleClose();
    }
  };

  const handleOpenImagePopup = (idx) => {
    setImagePopupState({ open: true, sectionIndex: idx });
  };

  const handleCloseImagePopup = () => {
    setImagePopupState({ open: false, sectionIndex: null });
  };

  const buildImageSnippet = (urls) => {
    const validUrls = urls.map((u) => u.trim()).filter((u) => u);
    if (validUrls.length === 0) return "";

    const objects = validUrls.map(
      (url) => `{"type": "image", "url": "${url}"}`
    );

    const body = `[${objects.join(",\n")}]`;

    return `\`\`\`json\n${body}\n\`\`\``;
  };

  const handleInsertImages = (urls) => {
    const imageUrls = urls || [];
    const { sectionIndex } = imagePopupState;
    if (
      sectionIndex == null ||
      sectionIndex < 0 ||
      sectionIndex >= items.length
    )
      return;

    const snippet = buildImageSnippet(imageUrls);
    if (!snippet) return;

    setItems((prev) => {
      const updated = [...prev];
      const current = updated[sectionIndex] || {};
      const text = String(current.content || "");

      const sel = selectionMap[sectionIndex] || {};
      const start =
        typeof sel.start === "number" &&
        sel.start >= 0 &&
        sel.start <= text.length
          ? sel.start
          : text.length;

      const before = text.slice(0, start);
      const after = text.slice(start);
      const insertion =
        before && !before.endsWith("\n") ? `\n${snippet}\n` : `${snippet}\n`;

      updated[sectionIndex] = {
        ...current,
        content: `${before}${insertion}${after}`,
      };

      return updated;
    });

    handleCloseImagePopup();
  };

  useEffect(() => {
    if (!open) return;
    const normalize = (val) => {
      try {
        if (Array.isArray(val)) {
          return val.map((it) => {
            if (typeof it === "string")
              return {
                type: (pageData && pageData.page_type) || "content",
                content: it,
              };
            if (it && typeof it === "object") {
              const t =
                it.type || (pageData && pageData.page_type) || "content";
              const c =
                typeof it.content === "string"
                  ? it.content
                  : JSON.stringify(it.content, null, 2);
              return { type: t, content: c };
            }
            return {
              type: (pageData && pageData.page_type) || "content",
              content: String(it),
            };
          });
        }
        if (typeof val === "string")
          return [
            {
              type: (pageData && pageData.page_type) || "content",
              content: val,
            },
          ];
        if (val && typeof val === "object") {
          if ("content" in val) {
            return [
              {
                type: val.type || (pageData && pageData.page_type) || "content",
                content:
                  typeof val.content === "string"
                    ? val.content
                    : JSON.stringify(val.content, null, 2),
              },
            ];
          }
          return [
            {
              type: (pageData && pageData.page_type) || "content",
              content: JSON.stringify(val, null, 2),
            },
          ];
        }
      } catch (e) {
        return [
          {
            type: (pageData && pageData.page_type) || "content",
            content: String(val ?? ""),
          },
        ];
      }
      return [];
    };
    setItems(normalize(initialValue));
  }, [open, initialValue, pageData]);

  return (
    <div
      className={`markdown-editor-popup ${open ? "" : "hidden"}`}
      onClick={handleClose}
    >
      <div
        className="markdown-editor-popup-body"
        style={{ maxWidth: items?.length > 0 ? "1100px" : "500px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="markdown-editor-popup-header">
          <h2>{title || "Edit Content"}</h2>
          <button
            className="markdown-editor-popup-close-button"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="markdown-editor-popup-content">
          {items.map((it, idx) => (
            <div key={idx} className="markdown-editor-wrapper">
              <div
                className="markdown-editor-section-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  Section {idx + 1} {it.type ? `(type: ${it.type})` : ""}
                </span>
                <button
                  type="button"
                  className="action-button"
                  onClick={() => handleOpenImagePopup(idx)}
                >
                  Add Image
                </button>
              </div>
              <MDEditor
                value={it.content}
                onChange={(v) => {
                  const val = v || "";
                  setItems((prev) =>
                    prev.map((p, i) => (i === idx ? { ...p, content: val } : p))
                  );
                }}
                height={400}
                preview="live"
                theme="light"
                previewOptions={mdPreviewOptions}
                textareaProps={{
                  onSelect: (event) => {
                    const target = event.target;
                    if (!target) return;
                    const { selectionStart, selectionEnd } = target;
                    setSelectionMap((prev) => ({
                      ...prev,
                      [idx]: { start: selectionStart, end: selectionEnd },
                    }));
                  },
                  onKeyUp: (event) => {
                    const target = event.target;
                    if (!target) return;
                    const { selectionStart, selectionEnd } = target;
                    setSelectionMap((prev) => ({
                      ...prev,
                      [idx]: { start: selectionStart, end: selectionEnd },
                    }));
                  },
                  onClick: (event) => {
                    const target = event.target;
                    if (!target) return;
                    const { selectionStart, selectionEnd } = target;
                    setSelectionMap((prev) => ({
                      ...prev,
                      [idx]: { start: selectionStart, end: selectionEnd },
                    }));
                  },
                }}
              />
            </div>
          ))}
        </div>
        <div className="markdown-editor-popup-actions">
          <button
            className="markdown-editor-popup-submit-button"
            onClick={handleSave}
            disabled={isSaveDisabled || saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {imagePopupState.open &&<AddImagePopup
          open={imagePopupState.open}
          handleClose={handleCloseImagePopup}
          onInsert={handleInsertImages}
        />}
      </div>
    </div>
  );
};

MarkdownEditorPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  initialValue: PropTypes.string,
  onSave: PropTypes.func,
  context: PropTypes.string,
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pageData: PropTypes.any,
  courseKey: PropTypes.string,
  subjectKey: PropTypes.string,
  topicKey: PropTypes.string,
  subTopicKey: PropTypes.string,
};

MarkdownEditorPopup.defaultProps = {
  title: "Edit Markdown Content",
  initialValue: "",
  onSave: null,
  context: undefined,
};

export default MarkdownEditorPopup;
